import { SetStateAction, useEffect, useState } from 'react';
import { Bonus, useDebouncedValue, useGetAvailableBonuses } from 'react-easyrocket';
import { useModalContext } from 'src/hooks/useModalContext';

interface usePromoCodeProps {
  selectedBonus: Bonus | null;
  setSelectedBonus: (bonus: Bonus | null) => void;
  setShowDepositBonuses: (value: SetStateAction<boolean>) => void;
  isLoadingDepositMethods: boolean;
}

export const usePromocode = ({
  selectedBonus,
  setSelectedBonus,
  setShowDepositBonuses,
  isLoadingDepositMethods
}: usePromoCodeProps) => {
  const [promoCode, setPromoCode] = useState<string>('');
  const [isPromocodeError, setPromocodeError] = useState(false);
  const { modalProps } = useModalContext<{ promocode?: string }>();

  const handleChangePromoCode = (code: string) => {
    if (!code) {
      setPromocodeError(false);
    }
    setPromoCode(code.toUpperCase());
  };

  const debouncedPromocode = useDebouncedValue(promoCode, 1000);

  useEffect(() => {
    if (modalProps?.promocode && !isLoadingDepositMethods) {
      handleChangePromoCode(modalProps?.promocode);
    }
  }, [modalProps?.promocode, isLoadingDepositMethods]);

  const { isFetching: isLoadingCheckPromocode } = useGetAvailableBonuses(
    { promocode: debouncedPromocode },
    {
      enabled: Boolean(debouncedPromocode) && debouncedPromocode.length < MAX_PROMOCODE_LENGTH,
      onError: () => {
        if (selectedBonus?.promocode !== promoCode) {
          setSelectedBonus(null);
        }
        setPromocodeError(true);
      },
      onSuccess: (data) => {
        if (data?.data?.type !== 5) {
          setPromocodeError(true);
          return;
        }
        setPromocodeError(false);
        if (data.data) {
          if (selectedBonus?.id !== data.data.id) {
            setShowDepositBonuses(true);
            setSelectedBonus(data.data);
          }
        }
      },
      cacheTime: 300_000
    }
  );

  return { promoCode, handleChangePromoCode, isPromocodeError, isLoadingCheckPromocode };
};

const MAX_PROMOCODE_LENGTH = 20;
